import { getApiErrorCode } from '@/utils/errorHandling'

/**
 * The `code` the admin API answers a 401 with when the token is valid but the
 * admin session's `expiresAt` has passed (`AdminAuthenticationFilter`, #4676).
 */
export const ADMIN_SESSION_EXPIRED_CODE = 'admin-session-expired'

const ENDED_KEY = 'scrivio.adminSessionExpired'

/**
 * Survives Keycloak's sign-out, which leaves the page and comes back to the login page through a
 * full load with no query. In `sessionStorage` so it stays with the tab whose session ended.
 */
export function rememberAdminSessionExpired(): void {
  sessionStorage.setItem(ENDED_KEY, 'true')
}

/** Reads the recorded expiry and forgets it, so it is shown once. */
export function takeAdminSessionExpired(): boolean {
  const expired = sessionStorage.getItem(ENDED_KEY) !== null
  sessionStorage.removeItem(ENDED_KEY)
  return expired
}

type AdminSessionExpiredHandler = () => void

let handler: AdminSessionExpiredHandler | null = null

/**
 * Register what ends the admin session when the backend reports it expired.
 *
 * The axios interceptor cannot reach the sign-out itself: it lives in
 * `router/admin-guard.ts`, which imports the API service. So the guard's
 * session watch registers here and the interceptor only reports.
 *
 * @returns a function that removes the registration again.
 */
export function onAdminSessionExpired(next: AdminSessionExpiredHandler): () => void {
  handler = next
  return () => {
    if (handler === next) handler = null
  }
}

/**
 * Hand an API error to the registered handler when it is the backend's
 * `admin-session-expired` refusal.
 *
 * @returns whether the error was that refusal and a handler took it — the
 *   caller then skips its token-refresh retry, which cannot help: a fresh token
 *   carries the same expired claim.
 */
export function reportAdminSessionExpired(error: unknown): boolean {
  if (handler === null) return false
  const status = (error as { response?: { status?: number } } | null)?.response?.status
  if (status !== 401 || getApiErrorCode(error) !== ADMIN_SESSION_EXPIRED_CODE) return false
  handler()
  return true
}
